import React, { useRef, useState, useEffect, useCallback } from 'react';

const THRESHOLD = 80;
const MAX_PULL = 120;

export default function PullToRefresh({ children }) {
  const [pullDistance, setPullDistance] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const startY = useRef(0);
  const pulling = useRef(false);

  const isAtTop = useCallback(() => window.scrollY <= 0, []);

  useEffect(() => {
    const onTouchStart = (e) => {
      if (refreshing || !isAtTop()) return;
      // A page whose DOCUMENT cannot scroll -- a fixed shell with
      // html/body overflow:hidden, e.g. the delegate Control Center -- keeps
      // window.scrollY pinned at 0 forever. isAtTop() is then true on EVERY
      // touch anywhere on the page, so every downward drag armed the pull and
      // hit e.preventDefault() on the non-passive touchmove below, cancelling
      // the native scroll of whatever inner container the finger was on. That
      // is what made scrolling up stick. Pull-to-refresh is meaningless on
      // such a page anyway, so stand down and let the container scroll.
      // Route bail. The delegate live Control Center is a fixed-shell page held
      // by an on-site coordinator; a stray pull that reloads it mid-event is
      // unacceptable, and pull-to-refresh has no meaning there.
      if (String(window.location.pathname || '').indexOf('/live/') === 0) return;
      // Ownership bail. If the gesture starts inside something that can scroll
      // vertically, that container owns it -- never arm the pull. This replaces
      // an earlier innerHeight test, which is unreliable on iOS because
      // innerHeight is the LAYOUT viewport and changes as the Safari toolbar
      // collapses, so the guard flipped on and off mid-session.
      var n = e.target;
      while (n && n !== document.body && n.nodeType === 1) {
        if (n.scrollHeight > n.clientHeight) {
          var oy = window.getComputedStyle(n).overflowY;
          if (oy === 'auto' || oy === 'scroll') return;
        }
        n = n.parentElement;
      }
      // Document bail. A page that pins html/body to overflow:hidden cannot
      // scroll at all, so there is nothing to pull.
      if (window.getComputedStyle(document.documentElement).overflow === 'hidden'
          || window.getComputedStyle(document.body).overflow === 'hidden') return;
      startY.current = e.touches[0].clientY;
      pulling.current = true;
    };

    const onTouchMove = (e) => {
      if (!pulling.current || refreshing) return;
      const dy = e.touches[0].clientY - startY.current;
      if (dy < 0) { pulling.current = false; setPullDistance(0); return; }
      // Resist pull with diminishing returns
      const dist = Math.min(dy * 0.45, MAX_PULL);
      if (dist > 10) e.preventDefault();
      setPullDistance(dist);
    };

    const onTouchEnd = () => {
      if (!pulling.current) return;
      pulling.current = false;
      if (pullDistance >= THRESHOLD) {
        setRefreshing(true);
        setPullDistance(THRESHOLD);
        setTimeout(() => window.location.reload(), 300);
      } else {
        setPullDistance(0);
      }
    };

    document.addEventListener('touchstart', onTouchStart, { passive: true });
    document.addEventListener('touchmove', onTouchMove, { passive: false });
    document.addEventListener('touchend', onTouchEnd, { passive: true });
    return () => {
      document.removeEventListener('touchstart', onTouchStart);
      document.removeEventListener('touchmove', onTouchMove);
      document.removeEventListener('touchend', onTouchEnd);
    };
  }, [pullDistance, refreshing, isAtTop]);

  const progress = Math.min(pullDistance / THRESHOLD, 1);

  return (
    <>
      {/* Pull indicator */}
      <div
        data-testid="pull-to-refresh-indicator"
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 9999,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: pullDistance > 5 ? `${pullDistance}px` : 0,
          overflow: 'hidden',
          transition: pulling.current ? 'none' : 'height 0.25s ease-out',
          pointerEvents: 'none',
          background: 'rgba(15, 15, 26, 0.85)',
          backdropFilter: 'blur(8px)',
        }}
      >
        <div
          style={{
            width: 28,
            height: 28,
            border: '3px solid rgba(197, 160, 89, 0.25)',
            borderTopColor: '#C5A059',
            borderRadius: '50%',
            opacity: progress,
            transform: refreshing ? 'none' : `rotate(${progress * 360}deg)`,
            animation: refreshing ? 'ptr-spin 0.6s linear infinite' : 'none',
            transition: refreshing ? 'none' : 'transform 0.1s ease',
          }}
        />
      </div>
      <style>{`@keyframes ptr-spin { to { transform: rotate(360deg); } }`}</style>
      {/* Page content shifted down during pull */}
      <div
        style={{
          transform: pullDistance > 5 ? `translateY(${pullDistance}px)` : 'none',
          transition: pulling.current ? 'none' : 'transform 0.25s ease-out',
        }}
      >
        {children}
      </div>
    </>
  );
}
